import { useEffect, useRef, useState } from 'react';
import { websocketService } from '../services/websocketService';

interface UseWebSocketProps {
  autoConnect?: boolean;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onNewDeal?: (deal: any) => void;
  onDealUpdated?: (update: any) => void;
  onNewComment?: (comment: any) => void;
  onNotification?: (notification: any) => void;
  onError?: (error: any) => void;
}

interface WebSocketHook {
  isConnected: boolean;
  connect: () => Promise<boolean>;
  disconnect: () => void;
  authenticate: (userId: string, username: string) => void;
  subscribeToDeals: () => void;
  subscribeToComments: (dealId: string) => void;
  subscribeToNotifications: (userId: string) => void;
  connectionInfo: any;
}

export const useWebSocket = (props: UseWebSocketProps = {}): WebSocketHook => {
  const {
    autoConnect = true,
    onConnect,
    onDisconnect,
    onNewDeal,
    onDealUpdated,
    onNewComment,
    onNotification,
    onError,
  } = props;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<any>(null);
  const callbacksRef = useRef(props);

  // Update callbacks ref when props change
  useEffect(() => {
    callbacksRef.current = props;
  }, [props]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      websocketService.disconnect();
    };
  }, [autoConnect]);

  // Update connection status
  useEffect(() => {
    const checkConnection = () => {
      const connected = websocketService.isConnected();
      setIsConnected(connected);
      setConnectionInfo(websocketService.getConnectionInfo());
    };

    // Check immediately
    checkConnection();

    // Check periodically
    const interval = setInterval(checkConnection, 1000);

    return () => clearInterval(interval);
  }, []);

  const connect = async (): Promise<boolean> => {
    try {
      const success = await websocketService.connect({
        onConnect: () => {
          setIsConnected(true);
          setConnectionInfo(websocketService.getConnectionInfo());
          onConnect?.();
        },
        onDisconnect: (reason: string) => {
          setIsConnected(false);
          setConnectionInfo(websocketService.getConnectionInfo());
          onDisconnect?.(reason);
        },
        onNewDeal: (deal: any) => {
          onNewDeal?.(deal);
        },
        onDealUpdated: (update: any) => {
          onDealUpdated?.(update);
        },
        onNewComment: (comment: any) => {
          onNewComment?.(comment);
        },
        onNotification: (notification: any) => {
          onNotification?.(notification);
        },
        onError: (error: any) => {
          console.error('WebSocket error in hook:', error);
          onError?.(error);
        },
      });

      setIsConnected(success);
      setConnectionInfo(websocketService.getConnectionInfo());
      return success;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      onError?.(error);
      return false;
    }
  };

  const disconnect = (): void => {
    websocketService.disconnect();
    setIsConnected(false);
    setConnectionInfo(websocketService.getConnectionInfo());
  };

  const authenticate = (userId: string, username: string): void => {
    websocketService.authenticate(userId, username);
  };

  const subscribeToDeals = (): void => {
    websocketService.subscribeToDeals();
  };

  const subscribeToComments = (dealId: string): void => {
    websocketService.subscribeToComments(dealId);
  };

  const subscribeToNotifications = (userId: string): void => {
    websocketService.subscribeToNotifications(userId);
  };

  return {
    isConnected,
    connect,
    disconnect,
    authenticate,
    subscribeToDeals,
    subscribeToComments,
    subscribeToNotifications,
    connectionInfo,
  };
};
